<template>
  <div class="vendor-sub-accounts">
    <div class="row align-right">
      <slot name="add-sub-account" />
      <button
        v-if="allowEdit"
        v-hkldreset
        type="button"
        class="btn btn--darkblue"
        role="cancel-button"
        @click="cancelEditSub()"
      >
        {{ $t('action.cancel') }}
      </button>
      <DialogSlot
        v-show="allowEdit"
        id="settings-users-contractor-sub-save"
        ref="update-sub-vendor"
        class="padding"
        :showCloseBtn="true"
        :modalBtn="$t('action.save')"
        :beforeOpenModal="validate"
      >
        <div slot="content">
          <span class="modal-pure-text">{{ $t('confirmSave') }}</span>
        </div>
        <div slot="buttons">
          <button
            v-if="allowEdit"
            v-hkldreset
            type="button"
            class="btn btn--orange"
            role="confirm-button"
            @click="updateSub()"
          >
            {{ $t('action.yesContinue') }}
          </button>
        </div>
      </DialogSlot>
    </div>
    <table>
      <thead>
        <tr>
          <th
            v-for="(item, index) in headerItems"
            :key="index"
          >
            {{ item }}
          </th>
          <th
            v-if="isContractorAdmin && (allowInvite || allowEdit)"
            style="width: 60px;"
          />
        </tr>
      </thead>
      <tbody>
        <tr />  <!-- cater the filter row (style) in Settings table -->
        <tr
          v-for="(account, index) in subAccounts"
          :key="index"
        >
          <td class="username">
            <InputField
              :showColon="false"
              :showFlag="false"
              :value="account.username"
              :readonly="true"
            />
          </td>
          <td class="name">
            <InputField
              :showColon="false"
              :showFlag="false"
              :value="getDisplayName(account)"
              :readonly="!allowEdit"
              required
              @updateValue="val => updateName(account, val)"
            />
          </td>
          <td
            v-for="(perm, permType) in subAccPermsMap"
            :key="permType"
          >
            <input
              v-if="account.perms[permType] || allowEdit"
              v-hkldinput="{ component: 'sub-contractors' }"
              type="checkbox"
              :checked="account.perms[permType]"
              :disabled="disableCheckbox(permType)"
              @change="event => updatePerms(account, permType, event.target.checked)"
            >
            <span v-else>-</span>
          </td>
          <td>
            <span>{{ formatDate(account.lastLoginAt) }}</span>
          </td>
          <td>
            <span>{{ formatDate(account.invitedAt) }}</span>
          </td>
          <td v-if="isContractorAdmin && (allowInvite || allowEdit)">
            <button
              v-if="allowInvite && !allowEdit"
              class="btn btn--darkblue btn--invite"
              role="invite-button"
              @click="() => inviteUser(account)"
            >{{ $t('action.invite') }}</button>
            <button
              v-if="allowEdit"
              class="fa fa-times fa-lg"
              role="remove-button"
              @click="() => removeAccount(index)"
            />
          </td>
        </tr>
      </tbody>
    </table>
    <HKLInviteModal
      ref="inviteModal"
      :listTitle="$t('settings.user.vendor.vendorUsers')"
      :accountsList="needInviteAccounts"
      @onInviteClick="() => inviteUser(needInviteAccounts)"
    />
  </div>
</template>

<script>
import { mapGetters } from 'vuex'
import lodash from 'lodash'
import { getCurrentInstance, computed, unref } from 'vue-demi'
import * as accountsRequest from '@/api/accounts/request'
import InputField from '@/controls/InputField'
import DialogSlot from '@/controls/DialogSlot'
import HKLInviteModal from '@/controls/Modal/InviteModal'
import { contactTypes } from '@/api/users/consts'
import { utils as formUtils } from '@/plugin/InputChange'
import { fusionInputCheckUtil } from '@/plugin/FusionInputCheck'
import { EmailTypes } from '@/utils/consts'
import dateUtil from '@/utils/dateUtil'
import EventBus from '@/utils/eventBus'
import userHelper from '@/utils/userHelper'
import * as regex from '@/utils/regex'
import { Rcs } from '@/utils/aclHelper'
import { getMaxNumOfVendorSubAccount } from '@/api/settings/request'
import { useRefModal } from '@/controls/Modal/modal.hook'
import { ContractorSubAccPermsMap, vendorSubAccTableElements } from './consts'

export default {
  name: 'VendorSubList',
  components: {
    InputField,
    DialogSlot,
    HKLInviteModal,
  },
  props: {
    allowEdit: Boolean,
    allowEditSwp: Boolean,
    allowInvite: Boolean,
    accountId: {
      type: String,
      default: '',
    },
    accountName: {
      type: String,
      default: '',
    },
    activeDepartmentId: {
      type: String,
      default: '',
    },
  },
  setup (props) {
    const { proxy } = getCurrentInstance()
    let returnData = {}
    // #region InviteAllModal
    const { open, close: closeInviteAllModal, modal } = useRefModal()
    const needInviteAccounts = computed(() => {
      return unref(proxy.subAccounts).filter((account) => !account.invitedAt)
    })
    returnData = {
      ...returnData,
      inviteModal: modal,
      needInviteAccounts,
      openInviteAllModal: () => {
        if (unref(props.allowInvite) && unref(needInviteAccounts).length > 0) {
          open()
        }
      },
      inviteUser: async (users) => {
        await Promise.all(
          lodash.castArray(users).map(async (user) => {
            const subContact = user.contacts.filter(contact => contact.type === contactTypes.NAME)[0]
            const subContactName = subContact && subContact.value
            const metaData = {
              id: user.id,
              name: props.accountName,
              adminContact: userHelper.getUserName(user.username, subContactName),
              template: EmailTypes.settings.createPassword,
              departmentId: props.activeDepartmentId,
            }

            await accountsRequest.inviteAccount(user.id, metaData, 'inviteCreatedContractor')
          }),
        )
        closeInviteAllModal()
        EventBus.emit('refresh:vendor-sub-accounts')
      },
    }
    // #endregion InviteAllModal
    return returnData
  },
  data () {
    return {
      ContractorSubAccPermsMap,
      subAccounts: [],
      bkSubAccounts: [],
      maxNumOfVendorSubAccount: 0,
      regex,
      allSubAccounts: [],
    }
  },
  computed: {
    ...mapGetters('user', ['currentUser', 'isContractorAdmin']),
    ...mapGetters('regions', ['enableTenderFeature', 'enableProjectFeature', 'enableWorkPermitsFeature', 'enableEcircularFeature']),
    headerItems () {
      const moduleHeadersKeyMap = this.moduleKeysMapGenerator([
        Rcs.ContractorTender,
        Rcs.ContractorProject,
        Rcs.StandaloneWorkPermits,
        Rcs.ContractorSettings,
      ])
      let tmpHeaderItems = lodash.cloneDeep(vendorSubAccTableElements())
      for (let [key, value] of Object.entries(moduleHeadersKeyMap)) {
        if (!this[key]) delete tmpHeaderItems[value]
      }
      return tmpHeaderItems
    },
    subAccPermsMap () {
      const modulePermsKeyMap = this.moduleKeysMapGenerator([
        Rcs.ContractorTender,
        Rcs.ContractorProject,
        Rcs.StandaloneWorkPermits,
        Rcs.ContractorSettings,
      ])
      let result = lodash.cloneDeep(ContractorSubAccPermsMap)
      for (let [key, value] of Object.entries(modulePermsKeyMap)) {
        if (!this[key]) delete result[value]
      }
      return result
    },
  },
  watch: {
    async accountId (nVal) {
      if (nVal) {
        await this.loadSubAccounts()
      }
    },
    async activeDepartmentId (nVal) {
      const newAccounts = this.allSubAccounts.find((account) => account.departmentId === nVal) || []
      this.subAccounts = deep(newAccounts, 'users') || []
      this.bkSubAccounts = lodash.cloneDeep(this.subAccounts)
      this.$emit('close:edit', !this.subAccounts.length)
      this.$emit('allow:add', this.subAccounts.length < this.maxNumOfVendorSubAccount)
    },
  },

  async mounted () {
    EventBus.$on('refresh:vendor-sub-accounts', async () => {
      await this.loadSubAccounts()
    })
    EventBus.$on('refresh-and-invite:vendor-sub-accounts', async () => {
      await this.loadSubAccounts()
      this.openInviteAllModal()
    })
    this.maxNumOfVendorSubAccount = await getMaxNumOfVendorSubAccount()
  },
  destroyed () {
    EventBus.$off('refresh:vendor-sub-accounts')
    EventBus.$off('refresh-and-invite:vendor-sub-accounts')
  },
  methods: {
    formatDate (date) {
      return date ? dateUtil.formatDate(date, 'DD MMM YY HH:mm') : '-'
    },
    async loadSubAccounts () {
      if (!this.accountId) return
      const accounts = await accountsRequest.findAllSubAccounts(this.accountId)
      if (!lodash.isArray(accounts) || accounts.length === 0) return
      this.allSubAccounts = accounts
      const newAccounts = accounts.find((account) => account.departmentId === this.activeDepartmentId) || []
      this.subAccounts = lodash.get(newAccounts, 'users', [])
      this.bkSubAccounts = lodash.cloneDeep(this.subAccounts)
      this.$emit('hide:edit', !this.subAccounts.length || (this.subAccounts.length && this.allowEdit))
      this.$emit('allow:add', this.subAccounts.length < this.maxNumOfVendorSubAccount)
    },
    getDisplayName (user) {
      const contacts = deep(user, 'contacts') || []
      const names = contacts.map(contact => {
        if (contact.type === 'NAME') {
          return contact.value
        }
      })
      return names[0]
    },
    updateName (user, val) {
      const contacts = deep(user, 'contacts') || []
      if (!contacts.length) {
        contacts.push({
          type: 'NAME',
          description: 'Name',
          userId: user.id, // TODO: @Michelle need to change to 'sourceId' when migrating to TMS as userId is deprecated.
          value: val,
        })
        user.contacts = contacts
      } else {
        contacts.map(contact => {
          if (contact.type === 'NAME') {
            contact.value = val
          }
        })
      }
      formUtils.setDirty('sub-contractors')
    },
    updatePerms (account, permType, checked) {
      if (!account.perms) {
        account.perms = {}
      }
      checked ? account.perms[permType] = ContractorSubAccPermsMap[permType] : delete account.perms[permType]
      formUtils.setDirty('sub-contractors')
    },
    removeAccount (index) {
      this.subAccounts.splice(index, 1)
    },
    cancelEditSub () {
      this.subAccounts = lodash.cloneDeep(this.bkSubAccounts)
      this.$forceUpdate()
      formUtils.resetDirty('sub-contractors')
      this.$emit('close:edit', !this.subAccounts.length)
    },
    disableCheckbox (permType) {
      const isSwp = permType === Rcs.StandaloneWorkPermits
      const isDisableSwp = isSwp && this.isContractorAdmin && !this.allowEditSwp
      return !this.allowEdit || isDisableSwp
    },

    validate () {
      fusionInputCheckUtil.validateAllFields({allowEmpty: 'none'}, '.vendor-sub-accounts')

      return $('.vendor-sub-accounts input.is-invalid-input').length === 0 &&
        fusionInputCheckUtil.isAllFieldsValid('.vendor-sub-accounts')
    },

    async updateSub () {
      await accountsRequest.updateSubAccountDetail(this.accountId, {
        departmentId: this.activeDepartmentId,
        subAccounts: this.subAccounts,
      })
      formUtils.resetDirty('sub-contractors')
      this.$refs['update-sub-vendor'].closeModal()
      await this.loadSubAccounts()
      this.$emit('close:edit', !this.subAccounts.length)
    },
    moduleKeysMapGenerator (orderedArr) {
      const moduleKeys = ['enableTenderFeature', 'enableProjectFeature', 'enableWorkPermitsFeature', 'enableEcircularFeature']
      let result = {}
      moduleKeys.forEach((value, index) => {
        const key = moduleKeys[index]
        result[key] = orderedArr[index]
      })
      return result
    },
  },
}
</script>

<style lang="scss" scoped>
@import '~styles/popup';
@import '~styles/settings/style';

.vendor-sub-accounts {
  th {
    text-align: center;
  }
  th:first-child, td:first-child, th:nth-child(2), td:nth-child(2) {
    text-align: left;
  }
  .name {
    padding: 8px;
  }
  .btn {
    float: right;
    margin: 0 5px 5px;
  }
  .btn--invite {
    zoom: .8;
    margin: 5px;
  }

  .padding {
    margin-right: 5px;
  }
}
</style>
